export type AuthExitReason =
  | 'session_expired'
  | 'password_changed'
  | 'signed_in_elsewhere'
  | 'signed_out_elsewhere';

export type AuthSyncEventType = 'login' | 'logout' | 'session_expired' | 'password_changed';

export interface AuthSyncEvent {
  type: AuthSyncEventType;
  at: number;
  sourceId: string;
}

export interface AuthUnauthorizedEventDetail {
  reason?: AuthExitReason;
  status?: number;
  source?: string;
}

export const AUTH_UNAUTHORIZED_EVENT = 'auth:unauthorized';

const AUTH_SYNC_STORAGE_KEY = 'auth:sync-event';
const AUTH_EXIT_REASON_STORAGE_KEY = 'auth:exit-reason';
const AUTH_SYNC_CHANNEL_NAME = 'auth-sync';
const AUTH_EXIT_REASON_MAX_AGE_MS = 5 * 60 * 1000;

const authSourceId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
let authSyncChannel: BroadcastChannel | null = null;

const canUseWindow = () => typeof window !== 'undefined';

const getAuthSyncChannel = (): BroadcastChannel | null => {
  if (!canUseWindow() || typeof window.BroadcastChannel === 'undefined') {
    return null;
  }
  if (!authSyncChannel) {
    authSyncChannel = new window.BroadcastChannel(AUTH_SYNC_CHANNEL_NAME);
  }
  return authSyncChannel;
};

const isAuthSyncEvent = (value: unknown): value is AuthSyncEvent => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<AuthSyncEvent>;
  const validType =
    candidate.type === 'login' ||
    candidate.type === 'logout' ||
    candidate.type === 'password_changed' ||
    candidate.type === 'session_expired';

  return (
    validType &&
    typeof candidate.at === 'number' &&
    Number.isFinite(candidate.at) &&
    typeof candidate.sourceId === 'string' &&
    candidate.sourceId.length > 0
  );
};

const parseAuthSyncEvent = (raw: unknown): AuthSyncEvent | null => {
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return isAuthSyncEvent(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  return isAuthSyncEvent(raw) ? raw : null;
};

const readStoredExitReason = (): AuthExitReason | null => {
  if (!canUseWindow()) {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(AUTH_EXIT_REASON_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as { reason?: AuthExitReason; at?: number };
    const reason = parsed.reason;
    const storedAt = parsed.at;

    const isKnownReason =
      reason === 'session_expired' ||
      reason === 'password_changed' ||
      reason === 'signed_in_elsewhere' ||
      reason === 'signed_out_elsewhere';

    if (!isKnownReason || typeof storedAt !== 'number') {
      window.sessionStorage.removeItem(AUTH_EXIT_REASON_STORAGE_KEY);
      return null;
    }

    if (Date.now() - storedAt > AUTH_EXIT_REASON_MAX_AGE_MS) {
      window.sessionStorage.removeItem(AUTH_EXIT_REASON_STORAGE_KEY);
      return null;
    }

    return reason;
  } catch {
    return null;
  }
};

export const dispatchUnauthorizedAuthEvent = (
  detail: AuthUnauthorizedEventDetail = {},
) => {
  if (!canUseWindow()) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<AuthUnauthorizedEventDetail>(AUTH_UNAUTHORIZED_EVENT, {
      detail,
    }),
  );
};

export const rememberAuthExitReason = (reason: AuthExitReason) => {
  if (!canUseWindow()) {
    return;
  }

  try {
    window.sessionStorage.setItem(
      AUTH_EXIT_REASON_STORAGE_KEY,
      JSON.stringify({ reason, at: Date.now() }),
    );
  } catch {
    // Ignore storage failures and continue auth flow.
  }
};

export const getStoredAuthExitReason = (): AuthExitReason | null =>
  readStoredExitReason();

export const clearStoredAuthExitReason = () => {
  if (!canUseWindow()) {
    return;
  }
  try {
    window.sessionStorage.removeItem(AUTH_EXIT_REASON_STORAGE_KEY);
  } catch {
    // Ignore storage failures and continue auth flow.
  }
};

export const consumeStoredAuthExitReason = (): AuthExitReason | null => {
  const reason = readStoredExitReason();
  clearStoredAuthExitReason();
  return reason;
};

export const broadcastAuthSyncEvent = (type: AuthSyncEventType) => {
  if (!canUseWindow()) {
    return;
  }

  const payload: AuthSyncEvent = {
    type,
    at: Date.now(),
    sourceId: authSourceId,
  };

  try {
    window.localStorage.setItem(AUTH_SYNC_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage failures and continue auth flow.
  }

  try {
    getAuthSyncChannel()?.postMessage(payload);
  } catch {
    // Ignore channel failures and continue auth flow.
  }
};

export const subscribeToAuthSyncEvents = (
  handler: (event: AuthSyncEvent) => void,
) => {
  if (!canUseWindow()) {
    return () => undefined;
  }

  const handleSyncPayload = (payload: AuthSyncEvent | null) => {
    if (!payload || payload.sourceId === authSourceId) {
      return;
    }
    handler(payload);
  };

  const onStorage = (event: StorageEvent) => {
    if (
      event.storageArea !== window.localStorage ||
      event.key !== AUTH_SYNC_STORAGE_KEY
    ) {
      return;
    }
    handleSyncPayload(parseAuthSyncEvent(event.newValue));
  };

  const channel = getAuthSyncChannel();
  const onChannelMessage = (event: MessageEvent<unknown>) => {
    handleSyncPayload(parseAuthSyncEvent(event.data));
  };

  window.addEventListener('storage', onStorage);
  channel?.addEventListener('message', onChannelMessage);

  return () => {
    window.removeEventListener('storage', onStorage);
    channel?.removeEventListener('message', onChannelMessage);
  };
};
